/**
 * Copyright 2020-present, TIENDUC.
 * All rights reserved.
 * @author ductt24@ghtk.vn on 11/08/2020.
 * @module CAMERA REDUCER
 */

import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import { MODULE_NAME, CHANGE_VIDEO_PARAM } from "./constants";
import { getParameterByName } from "core/helpers";
import _ from "lodash";
import { videoThunks } from "./thunks";

// Adapter
export const dvrAdapter = createEntityAdapter({
  selectId: dvr => dvr._id,
  sortComparer: (a, b) => a.dvr_name.localeCompare(b.dvr_name)
});
export const channelAdapter = createEntityAdapter({
  selectId: channel => channel._id,
  sortComparer: (a, b) => a.channel_name.localeCompare(b.channel_name)
});
export const scenarioAdapter = createEntityAdapter({
  selectId: scenario => scenario._id,
  sortComparer: (a, b) => a.scenario_name.localeCompare(b.scenario_name)
});
export const moduleAdapter = createEntityAdapter({
  selectId: module => module.module_id,
  sortComparer: (a, b) => a.module_name.localeCompare(b.module_name)
});
export const cartAdapter = createEntityAdapter({
  selectId: cart => cart.cart_id,
  sortComparer: (a, b) => a.cart_name.localeCompare(b.cart_name)
});
export const setupAdapter = createEntityAdapter({
  selectId: setup => setup._id,
  sortComparer: (a, b) => a.create_date.localeCompare(b.create_date)
});
export const dvrChannelAdapter = createEntityAdapter({
  selectId: dvrChannel => dvrChannel.dvr_channel_id
});
export const videoAdapter = createEntityAdapter({
  selectId: video => video._id
});

// DVR slice
export const dvrSlice = createSlice({
  name: `${MODULE_NAME}/dvr`,
  initialState: dvrAdapter.getInitialState({
    loading: false,
    online_devices: 0,
    count: 0
  }),
  reducers: {
    dvrAdded: dvrAdapter.addOne,
    dvrLoading(state) {
      state.loading = true;
    },
    dvrDeleted: dvrAdapter.removeOne,
    dvrBrowsed(state, action) {
      if (state.loading === true) {
        state.count = action.payload.count || 0;
        state.online_devices = action.payload.online_devices || 0;
        dvrAdapter.setAll(state, action.payload.data);
        state.loading = false;
      }
    },
    dvrEdited: dvrAdapter.updateOne
  }
});

export const cameraSlice = createSlice({
  name: `${MODULE_NAME}/camera`,
  initialState: {
    // camera_setting : {
    //   data : {}
    // },
    networkManagement: {
      data: {},
      count: {},
      station: {},
      cameraConfig: undefined
    },
    truckCameraManage: {
      license_plates: [],
      truck_camera_infos: [],
      list_sn: [],
      list_all_sn: [],
      all_license_plate: [],
      count: 0,
      total_setup_trucks: 0,
      total_online_cabins: 0,
      total_cabin_cameras: 0,
      total_online_trunks: 0,
      total_trunk_cameras: 0,
      total_auto_register_cameras: 0
    },
    userCameraPermission: {
      data: [],
      count: 0
    },
    userLogManage: {
      data: [],
      count: 0,
      loading: false
    }
  },
  reducers: {
    getUserCameraPermission(state, action) {
      state.userCameraPermission.data = action.payload.data;
      state.userCameraPermission.count = action.payload.count;
      return state;
    },
    getAllSn(state, action) {
      state.truckCameraManage.list_all_sn = action.payload;
      return state;
    },
    getAllLicensePlate(state, action) {
      state.truckCameraManage.all_license_plate = action.payload;
      return state;
    },
    getSN(state, action) {
      state.truckCameraManage.list_sn = action.payload;
      return state;
    },
    getTruckCamera(state, action) {
      state.truckCameraManage.truck_camera_infos = action.payload.data;
      state.truckCameraManage.count = action.payload.count;
      state.truckCameraManage.total_setup_trucks =
        action.payload.total_setup_trucks;
      state.truckCameraManage.total_online_cabins =
        action.payload.total_online_cabins;
      state.truckCameraManage.total_cabin_cameras =
        action.payload.total_cabin_cameras;
      state.truckCameraManage.total_online_trunks =
        action.payload.total_online_trunks;
      state.truckCameraManage.total_trunk_cameras =
        action.payload.total_trunk_cameras;
      state.truckCameraManage.total_auto_register_cameras =
        action.payload.total_auto_register_cameras;
      return state;
    },
    getLicensePlate(state, action) {
      state.truckCameraManage.license_plates = action.payload;
      return state;
    },
    browseNetwork(state, action) {
      state.networkManagement.data = action.payload.data;
      state.networkManagement.count = action.payload.count;
      return state;
    },

    browseStation(state, action) {
      state.networkManagement.station = action.payload;
      return state;
    },
    browseCameraSetting(state, action) {
      state.networkManagement.cameraConfig = action.payload;
      return state;
    },
    getUserLogManage(state, action) {
      state.userLogManage.data = action.payload.data;
      state.userLogManage.count = action.payload.count;
      return state;
    },
    userLogLoading(state, action) {
      state.userLogManage.loading = action.payload.loading;
      return state;
    }
  }
});
const getDefaultStationData = (station_id, station_type) => {
  return {
    station_id: station_id,
    station_type: station_type,
    scenarios: scenarioAdapter.getInitialState({
      loading: false
    }),
    setups: setupAdapter.getInitialState({
      loading: false
    }),
    dvrs: dvrAdapter.getInitialState({
      loading: false
    }),
    channels: channelAdapter.getInitialState({
      loading: false
    }),
    modules: moduleAdapter.getInitialState({
      loading: false
    }),
    carts: cartAdapter.getInitialState({
      loading: false
    })
  };
};

const cameraConfigByStationSlice = createSlice({
  name: `${MODULE_NAME}/config/station`,
  initialState: {
    station_id: getParameterByName("station_id"),
    station_type: getParameterByName("station_type"),
    station: getParameterByName("station_id")
      ? {
          [getParameterByName("station_id")]: getDefaultStationData(
            getParameterByName("station_id"),
            getParameterByName("station_type")
          )
        }
      : {},
    dvrChannel: dvrChannelAdapter.getInitialState({
      loading: false
    }),
    video: videoAdapter.getInitialState({
      loading: false,
      isMerge: false,
      showLoadMore: false,
      param: {
        pkg_order: "",
        station_id: undefined,
        time_start: undefined,
        action: "",
        time_stop: undefined,
        offset: 1,
        limit: 16
      },
      paramLog: {
        pkg_order: "",
        station_id: undefined,
        time_start: undefined,
        action: "",
        time_stop: undefined,
        offset: 1,
        limit: 20
      }
    }),
    config: {
      configByPlace: [],
      actions: {},
      typeOfGoods: []
    },
    cameraActionConfigsInfo: {
      data: []
    }
  },
  reducers: {
    getTypeOfGoods: (state, action) => {
      state.config.typeOfGoods = action.payload;
      return state;
    },
    getCameraActionConfigs: (state, action) => {
      state.cameraActionConfigsInfo.data = action.payload;
      return state;
    },
    getActionTypeActions: (state, action) => {
      state.config.actions = action.payload;
      return state;
    },
    cameraConfigByPlace: (state, action) => {
      state.config.configByPlace = action.payload;
      return state;
    },
    changeStation: (state, action) => {
      const { station_id, station_type } = action.payload;
      if (!station_id) {
        state.station_id = undefined;
        state.station_type = undefined;
        return state;
      }
      state.station_id = action.payload.station_id;
      state.station_type = action.payload.station_type;
      if (!state.station[station_id]) {
        state.station[station_id] = getDefaultStationData(
          station_id,
          station_type
        );
      }
    },
    scenarioLoading(state, action) {
      state.station[action.payload].scenarios.loading = true;
    },
    scenarioDeleted(state, action) {
      let { scenarioId, station_id } = action.payload;
      scenarioAdapter.removeOne(
        state.station[station_id].scenarios,
        scenarioId
      );
    },
    scenarioBrowsed(state, action) {
      const { station_id, data } = action.payload;
      const scenarios = state.station[station_id].scenarios;
      scenarioAdapter.setAll(scenarios, data);
      scenarios.loading = false;
    },
    scenarioEdited(state, action) {
      let { scenario, station_id } = action.payload;
      scenarioAdapter.updateOne(state.station[station_id].scenarios, {
        id: scenario._id,
        changes: scenario
      });
    },
    scenarioAdded(state, action) {
      let { scenario, station_id } = action.payload;
      scenarioAdapter.addOne(state.station[station_id].scenarios, scenario);
    },
    setupLoading(state, action) {
      state.station[action.payload].setups.loading = true;
    },
    setupDeleted(state, action) {
      let { setupId, station_id } = action.payload;
      setupAdapter.removeOne(state.station[station_id].setups, setupId);
    },
    setupBrowsed(state, action) {
      const { station_id, data } = action.payload;
      const setups = state.station[station_id].setups;
      setupAdapter.setAll(setups, data);
      setups.loading = false;
    },
    setupEdited(state, action) {
      let { setup, station_id } = action.payload;
      setupAdapter.updateOne(state.station[station_id].setups, {
        id: setup._id,
        changes: setup
      });
    },
    setupAdded(state, action) {
      let { setup, station_id } = action.payload;
      setupAdapter.addOne(state.station[station_id].setups, setup);
    },
    dvrLoading(state, action) {
      state.station[action.payload].dvrs.loading = true;
    },
    dvrBrowsed(state, action) {
      const { station_id, data } = action.payload;
      const dvrs = state.station[station_id].dvrs;
      state.server_no = _.get(data, "0.s_no");
      dvrAdapter.setAll(dvrs, data);
      dvrs.loading = false;
    },
    setServerNo(state, action) {
      const { serverNo } = action.payload;
      state.server_no = serverNo;
    },
    dvrChannelBrowsed(state, action) {
      dvrChannelAdapter.setAll(state.dvrChannel, action.payload);
    },
    channelLoading(state, action) {
      state.station[action.payload].channels.loading = true;
    },
    channelBrowsed(state, action) {
      const { station_id, data } = action.payload;
      const channels = state.station[station_id].channels;
      channelAdapter.setAll(channels, data);
      channels.loading = false;
    },
    videoLoading(state) {
      state.video.loading = true;
    },
    videoBrowsed(state, action) {
      const { data } = action.payload;
      let showLoadMore = true;
      if (_.size(data) < state.video.param.limit) {
        showLoadMore = false;
      }
      if (state.video.isMerge) {
        videoAdapter.upsertMany(state.video, data);
      } else {
        videoAdapter.setAll(state.video, data);
      }
      state.video.isMerge = false;
      state.video.loading = false;
      state.video.showLoadMore = showLoadMore;
    }
  },
  extraReducers: builder => {
    builder.addCase(CHANGE_VIDEO_PARAM, (state, action) => {
      const { isMerge = false, param } = action.payload;
      state.video.isMerge = isMerge;
      _.forEach(param, (fieldValue, fieldName) => {
        state.video.param[fieldName] = fieldValue;
      });
    });
  }
});

const cameraAISlice = createSlice({
  name: `${MODULE_NAME}/cameraAi`,
  initialState: {
    cameraAIManageInfo: {
      data: [],
      count: 0
    },
    cameraAIFirmwareInfo: {
      data: [],
      count: 0
    },
    cameraAIModelInfo: {
      data: [],
      count: 0
    },
    cameraAILogInfo: {
      data: [],
      count: 0
    }
  },
  reducers: {
    getCameraAIManageInfo: (state, action) => {
      state.cameraAIManageInfo.data = action.payload.data;
      state.cameraAIManageInfo.count = action.payload.count;
      return state;
    },
    getCameraAIFirmwareInfo: (state, action) => {
      state.cameraAIFirmwareInfo.data = action.payload.data;
      state.cameraAIFirmwareInfo.count = action.payload.count;
      return state;
    },
    getCameraAIModelInfo: (state, action) => {
      state.cameraAIModelInfo.data = action.payload.data;
      state.cameraAIModelInfo.count = action.payload.count;
      return state;
    },
    getCameraAILogInfo: (state, action) => {
      state.cameraAILogInfo.data = action.payload.data;
      state.cameraAILogInfo.count = action.payload.count;
      return state;
    }
  },
  extraReducers: builder => {}
});

export const dvrActions = dvrSlice.actions;
export const dvrReducer = dvrSlice.reducer;
export const cameraActions = cameraSlice.actions;
export const cameraReducer = cameraSlice.reducer;
export const cameraConfigByStationActions = cameraConfigByStationSlice.actions;
export const cameraConfigByStationReducer = cameraConfigByStationSlice.reducer;
export const cameraAIActions = cameraAISlice.actions;
export const cameraAIReducer = cameraAISlice.reducer;
